import React, { useMemo, useState } from 'react'
import { BoxPage } from '@/components/common/BoxPage'
import { BalanceTable } from '@/components/finance/balance/BalanceTable'
import { BalanceFilterBar } from '@/components/finance/balance/BalanceFilterBar'
import { MainStatisticChartCard } from '@/components/finance/balance/MainStatisticChartCard'
import { StatisticSummaryCards } from '@/components/finance/balance/StatisticSummaryCards'
import { CardContent, Card, Grid, Typography, IconButton, CircularProgress, Tab, Tabs, Box, Chip, Switch, Tooltip } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { Balance, IHttpError, SystemMaintenance, useUpdateSystemMaintenanceMutation } from '@/@data'
import { useNotification, useRouteQuery, useTranslator } from '@/hooks'
import { TotalTransactionFees } from '@/components/finance/balance/TotalTransactionFees'
import { TotalEWADisbursement } from '@/components/finance/balance/TotalEWADisbursement'
import { FinancialSummary } from '@/components/finance/balance/FinancialSummary'
import { BalancePastTable } from '@/components/finance/balance/BalancePastTable'

const BalancePage = () => {
  const { __, __n } = useTranslator()
  const n = useNotification()
  const { ConfirmDialog } = n

  const { searchQuery, updateQuery } = useRouteQuery()
  const [openStatistic, setOpenStatistic] = useState(true)
  const [tabValue, setTabValue] = useState(0)

  const [openPastStatistic, setOpenPastStatistic] = useState(true)

  const params = useMemo(() => {
    return {
      page: searchQuery.get('page'),
      perPage: searchQuery.get('limit'),
      company_id: searchQuery.get('company_id_table'),
      contract_from: searchQuery.get('contract_from'),
      contract_to: searchQuery.get('contract_to'),
      balance_status: searchQuery.get('balance_status'),
      year: searchQuery.get('month')?.substring(0, 4),
      month: searchQuery.get('month')?.substring(5, 7)
    }
  }, [searchQuery])

  const paramsEstimate = useMemo(() => {
    return {
      page: searchQuery.get('page'),
      perPage: searchQuery.get('limit'),
      company_id: searchQuery.get('company_id_table'),
      contract_from: searchQuery.get('contract_from'),
      contract_to: searchQuery.get('contract_to'),
      balance_status: searchQuery.get('balance_status'),
      year: searchQuery.get('month')?.substring(0, 4),
      month: searchQuery.get('month')?.substring(5, 7),
      is_est: 1
    }
  }, [searchQuery])

  const paramsActual = useMemo(() => {
    return {
      page: searchQuery.get('page'),
      perPage: searchQuery.get('limit'),
      company_id: searchQuery.get('company_id_table'),
      contract_from: searchQuery.get('contract_from'),
      contract_to: searchQuery.get('contract_to'),
      balance_status: searchQuery.get('balance_status'),
      year: searchQuery.get('month')?.substring(0, 4),
      month: searchQuery.get('month')?.substring(5, 7),
      is_act: 1
    }
  }, [searchQuery])

  const paramsStatisticFuture = useMemo(() => {
    return {
      page: searchQuery.get('page'),
      perPage: searchQuery.get('limit'),
      company_id: searchQuery.get('company_id_table'),
      contract_from: searchQuery.get('contract_from'),
      contract_to: searchQuery.get('contract_to'),
      balance_status: searchQuery.get('balance_status'),
      year: searchQuery.get('month')?.substring(0, 4),
      month: searchQuery.get('month')?.substring(5, 7)
    }
  }, [searchQuery])

  const paramsStatistic = useMemo(() => {
    const monthParam = searchQuery.get('month')
    const currentDate = new Date()
    const currentYearMonth = currentDate.getFullYear() + '-' + String(currentDate.getMonth() + 1).padStart(2, '0')
    const urlMonth = monthParam?.substring(0, 7) ?? ''
    const isCurrentMonthOrFuture = urlMonth === currentYearMonth

    const baseParams: any = {
      year: searchQuery.get('month')?.substring(0, 4),
      month: searchQuery.get('month')?.substring(5, 7),
      company_id: searchQuery.get('company_id')
    }

    if (isCurrentMonthOrFuture) {
      if (tabValue === 0) {
        baseParams.is_est = 1
      } else if (tabValue === 1) {
        baseParams.is_act = 1
      }
    }

    return baseParams
  }, [searchQuery, tabValue])

  const showPastMonth = useMemo(() => {
    const monthParam = searchQuery.get('month')

    if (!monthParam) return false

    const currentDate = new Date()
    const currentYearMonth = currentDate.getFullYear() + '-' + String(currentDate.getMonth() + 1).padStart(2, '0')
    const urlMonth = monthParam.substring(0, 7)

    // Return true only if urlMonth is before current month (past month)
    return urlMonth < currentYearMonth
  }, [searchQuery])

  const showFutureMonth = useMemo(() => {
    const monthParam = searchQuery.get('month')

    if (!monthParam) return false

    const currentDate = new Date()
    const currentYearMonth = currentDate.getFullYear() + '-' + String(currentDate.getMonth() + 1).padStart(2, '0')
    const urlMonth = monthParam.substring(0, 7)

    return urlMonth > currentYearMonth
  }, [searchQuery])

  const showCurrentMonth = useMemo(() => {
    const monthParam = searchQuery.get('month')

    if (!monthParam) return false

    const currentDate = new Date()
    const currentYearMonth = currentDate.getFullYear() + '-' + String(currentDate.getMonth() + 1).padStart(2, '0')
    const urlMonth = monthParam.substring(0, 7)

    return urlMonth === currentYearMonth
  }, [searchQuery])

  const { data: dataBalanceStatistics, refetch } = useQuery({
    ...Balance.getBalanceStatistics(params),
    enabled: !!params.month && !!showPastMonth
  })

  const { data: dataBalanceStatisticsEstimate } = useQuery({
    ...Balance.getBalanceStatistics(!showFutureMonth ? paramsEstimate : paramsStatisticFuture),
    enabled: !!(paramsEstimate.month || paramsStatisticFuture.month) && !!(showCurrentMonth || showFutureMonth)
  })

  const { data: dataBalanceStatisticsActual, refetch: refetchBalanceStatisticsActual } = useQuery({
    ...Balance.getBalanceStatistics(paramsActual),
    enabled: !!paramsActual.month && !!showCurrentMonth
  })

  const { data: dataBalanceStatisticsPast } = useQuery({
    ...Balance.getBalanceStatisticsCard(paramsStatistic),
    enabled: !!paramsStatistic.month
  })

  const { data: dataChartTotalTransactionFees } = useQuery({
    ...Balance.getChartTotalTransactionFees(paramsStatistic),
    enabled: !!paramsStatistic.month
  })

  const { data: dataChartTotalEwaDisbursement } = useQuery({
    ...Balance.getChartTotalEwaDisbursement(paramsStatistic),
    enabled: !!paramsStatistic.month
  })

  const {
    data: dataGetAmountBalance,
    refetch: amountRefech,
    isFetching: isRefreshingBalance
  } = useQuery({
    ...Balance.getBalanceAmount()
  })

  const partnerBalances = useMemo(
    () => Object.entries(dataGetAmountBalance?.data || {}),
    [dataGetAmountBalance]
  )

  const dataChartTotalEwaDisbursementMonth = useMemo(() => {
    const month = Number(searchQuery.get('month')?.substring(5, 7))

    return dataChartTotalEwaDisbursement?.data[month]?.balance
  }, [dataChartTotalEwaDisbursement, searchQuery])

  const dataBalanceStatisticsCompare = useQuery({
    ...Balance.getBalanceStatisticsCompare(paramsStatistic),
    enabled: !!paramsStatistic.month
  })

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
    updateQuery({ page: 1 })
  }

  const { data: dataSystemMaintenance, refetch: refetchSystemMaintenance, isFetching: isFetchingSystemMaintenance } = useQuery({
    ...SystemMaintenance.getSystemMaintenance()
  })

  const { mutate: updateSystemMaintenance, isPending: isUpdatingSystemMaintenance } = useUpdateSystemMaintenanceMutation()

  const handleChangeSystemMaintenance = () => {
    const newValue = !dataSystemMaintenance?.data?.is_maintenance

    n.confirm(
      newValue ? __('If you turn on the system maintenance, the system will be in maintenance mode and no one can access the system. Are you sure you want to update system maintenance?') : __('If you turn off the system maintenance, the system will be in normal mode and everyone can access the system. Are you sure you want to update system maintenance?'), {
      onConfirm: () => {
        updateSystemMaintenance({ is_maintenance: newValue }, {
          onSuccess: () => {
            n.success(__('System maintenance updated successfully'))
            refetchSystemMaintenance()
          },
          onError: (error: IHttpError) => {
            n.error(error.message)
          }
        })
      }
    })
  }

  return (
    <BoxPage
      title={__('Balance Management')}
      breadcrumbs={[{ label: __('Finance Management') }, { label: __('Balance Management'), href: '/cms/finance/balance' }]}
    >
      <div className='mb-[26px] flex flex-col gap-[26px]'>
        <Grid container spacing={6}>
          <Grid size={{ xs: 12, md: 9 }}>
            <BalanceFilterBar />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <div className={`relative h-full rounded-[16px] overflow-hidden ${dataSystemMaintenance?.data?.is_maintenance === false || isFetchingSystemMaintenance ? 'bg-[#3d5afe]' : 'bg-[#ff4c51]'}`}>
              <div className='absolute top-0 left-0 bottom-0 right-0 w-full h-full'>
                {dataSystemMaintenance?.data?.is_maintenance === false || isFetchingSystemMaintenance ? (
                  <img src="/images/bank/bg-card-balance.png" alt="bg" className='w-full h-full object-cover' />
                ) : (
                  <img src="/images/bank/bg-card-balance-off.png" alt="bg" className='w-full h-full object-cover' />
                )}
              </div>
              <div className='p-[18px] absolute bottom-0 left-0 right-0 top-0 z-10'>
                <div className='flex items-start justify-between flex-col h-full'>
                  <div>
                    <div className='flex items-center justify-between'>
                      <Typography variant='h5' className='text-white'>
                        {__('LIFT Fund Balance')}
                      </Typography>
                      <IconButton
                        size='small'
                        onClick={() => amountRefech()}
                        disabled={isRefreshingBalance}
                        sx={{
                          width: 30,
                          height: 30,
                          backgroundColor: 'unset',
                          color: '#fff',
                          '&:hover': {
                            backgroundColor: '#e0e0e0'
                          },
                          '&:disabled': {
                            color: '#fff',
                            opacity: 0.7
                          }
                        }}
                      >
                        {isRefreshingBalance ? (
                          <CircularProgress size={18} sx={{ color: '#fff' }} />
                        ) : (
                          <i className='tabler-refresh' style={{ fontSize: 18 }} />
                        )}
                      </IconButton>
                    </div>

                    <div className='flex items-center gap-0 ml-[-8px]'>
                      {isUpdatingSystemMaintenance || isFetchingSystemMaintenance ? (
                        <CircularProgress size={18} sx={{ color: '#fff' }} />
                      ) : (
                        <Switch
                          checked={dataSystemMaintenance?.data?.is_maintenance}
                          onChange={handleChangeSystemMaintenance}
                          title={__('System maintenance')}
                          size='medium'
                          color={dataSystemMaintenance?.data?.is_maintenance === false ? 'default' : '#ff4c51' as any}
                        />
                      )}
                      <div className='text-white text-[15px] font-medium'>
                        {__('Emergency')}
                      </div>
                    </div>
                  </div>
                  <div className='text-white'>
                    {partnerBalances.length === 0 ? (
                      <div className='text-[24px]'>-- ₫</div>
                    ) : (
                      <div className='flex flex-col gap-2'>
                        {partnerBalances.map(([partner, partnerBalance]) => (
                          <div key={partner} className='leading-tight'>
                            <div className='flex items-center justify-between gap-4'>
                              <div className='text-[13px] font-medium opacity-90'>
                                {partner.replace(/_/g, ' ')}
                              </div>
                              <div className='text-[18px] font-semibold flex items-center gap-2'>
                                {partnerBalance?.status ? `${__n(partnerBalance?.amount || 0)} ₫` : '-- ₫'}
                                {!partnerBalance?.status && !!partnerBalance?.reason && (
                                  <Tooltip title={partnerBalance.reason} arrow placement='top'>
                                    <span className='inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#ff4c51] text-white text-[10px] font-bold cursor-help'>
                                      !
                                    </span>
                                  </Tooltip>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Grid>
        </Grid>
      </div>

      <ConfirmDialog />

      {!showPastMonth ? (
        <Box className='flex flex-col gap-4'>
          {showCurrentMonth && (
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label='Current Balance Tabs'
              className='mb-4 '
              variant='fullWidth'
            >
              <Tab
                label={
                  <div className='flex flex-row gap-2 items-center'>
                    {__('Estimate Balance')}
                    <Chip
                      label={dataBalanceStatisticsEstimate?.meta?.total || '0'}
                      className='text-white bg-[#3551F2] rounded-full h-[18px]'
                    />
                  </div>
                }
              />
              <Tab
                label={
                  <div className='flex flex-row gap-2 items-center'>
                    {__('Actual Balance')}
                    <Chip
                      label={dataBalanceStatisticsActual?.meta?.total}
                      className='text-white bg-[#3551F2] rounded-full h-[18px]'
                    />
                  </div>
                }
              />
            </Tabs>
          )}

          {(tabValue === 0 || showFutureMonth) && (
            <div className='flex flex-col gap-[26px]'>
              {openStatistic && (
                <>
                  <Grid container spacing={6}>
                    <Grid size={{ xs: 12, md: 9 }}>
                      <MainStatisticChartCard
                        data={dataChartTotalTransactionFees?.data}
                        dataCompare={dataBalanceStatisticsCompare?.data?.data || []}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                      <StatisticSummaryCards dataCompare={dataBalanceStatisticsCompare?.data?.data || []} />
                    </Grid>
                  </Grid>
                </>
              )}
              <Card
                sx={{
                  borderRadius: 3
                }}
              >
                <CardContent sx={{ padding: 0 }}>
                  <BalanceTable
                    data={dataBalanceStatisticsEstimate?.data || []}
                    meta={dataBalanceStatisticsEstimate?.meta || {}}
                    openStatistic={openStatistic}
                    setOpenStatistic={() => setOpenStatistic(!openStatistic)}
                  />
                </CardContent>
              </Card>
            </div>
          )}
          {tabValue === 1 && showCurrentMonth && (
            <div className='flex flex-col gap-[26px]'>
              {openPastStatistic && (
                <>
                  <Grid container spacing={6}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TotalTransactionFees data={dataChartTotalTransactionFees?.data} />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TotalEWADisbursement
                        data={dataChartTotalEwaDisbursement?.data}
                        dataMonth={dataChartTotalEwaDisbursementMonth}
                      />
                    </Grid>
                  </Grid>
                  <FinancialSummary data={dataBalanceStatisticsPast?.data || []} />
                </>
              )}
              <Card
                sx={{
                  borderRadius: 3
                }}
              >
                <CardContent sx={{ padding: 0 }}>
                  <BalancePastTable
                    data={dataBalanceStatisticsActual?.data || []}
                    meta={dataBalanceStatisticsActual?.meta || {}}
                    openPastStatistic={openPastStatistic}
                    setOpenPastStatistic={() => setOpenPastStatistic(!openPastStatistic)}
                    refetch={refetchBalanceStatisticsActual}
                  />
                </CardContent>
              </Card>
            </div>
          )}
        </Box>
      ) : (
        <div className='flex flex-col gap-[26px]'>
          {openPastStatistic && (
            <>
              <Grid container spacing={6}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TotalTransactionFees data={dataChartTotalTransactionFees?.data} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TotalEWADisbursement
                    data={dataChartTotalEwaDisbursement?.data}
                    dataMonth={dataChartTotalEwaDisbursementMonth}
                  />
                </Grid>
              </Grid>
              <FinancialSummary data={dataBalanceStatisticsPast?.data || []} />
            </>
          )}
          <Card
            sx={{
              borderRadius: 3
            }}
          >
            <CardContent sx={{ padding: 0 }}>
              <BalancePastTable
                data={dataBalanceStatistics?.data || []}
                meta={dataBalanceStatistics?.meta || {}}
                openPastStatistic={openPastStatistic}
                setOpenPastStatistic={() => setOpenPastStatistic(!openPastStatistic)}
                refetch={refetch}
              />
            </CardContent>
          </Card>
        </div>
      )}
    </BoxPage>
  )
}

export default BalancePage
